import React from 'react';
import { useDrop, useDrag } from 'react-dnd';
import { useApp } from '../contexts/AppContext';
import { Skill } from '../types';
import { SkillIcon } from './SkillIcon';

interface ActionBarProps {
  barIndex: number;
  onSkillUse: (skill: Skill) => void;
  isSkillOnCooldown: (skill: Skill) => boolean;
  getCooldownRemaining: (skill: Skill) => number;
  getHighlightType?: (skill: Skill) => 'none' | 'combo' | 'proc' | 'resource' | 'ready';
  getHighlightStyle?: (highlightType: string) => any;
  isSkillUsable?: (skill: Skill) => boolean;
  getChargeInfo?: (skill: Skill) => { current: number; max: number; nextChargeIn: number } | null;
}

interface ActionSlotProps {
  skill: Skill | null;
  slotIndex: number;
  barIndex: number;
  keyBinding?: string;
  onSkillUse: (skill: Skill) => void;
  isSkillOnCooldown: (skill: Skill) => boolean;
  getCooldownRemaining: (skill: Skill) => number;
  getHighlightType?: (skill: Skill) => 'none' | 'combo' | 'proc' | 'resource' | 'ready';
  getHighlightStyle?: (highlightType: string) => any;
  isSkillUsable?: (skill: Skill) => boolean;
  getChargeInfo?: (skill: Skill) => { current: number; max: number; nextChargeIn: number } | null;
}

const ActionSlot: React.FC<ActionSlotProps> = ({ 
  skill, 
  slotIndex, 
  barIndex, 
  keyBinding, 
  onSkillUse,
  isSkillOnCooldown,
  getCooldownRemaining,
  getHighlightType,
  getHighlightStyle,
  isSkillUsable,
  getChargeInfo
}) => {
  const { state, updateActionBar } = useApp();

  // 拖拽源 - 从操作栏拖出技能
  const [{ isDragging }, drag] = useDrag({
    type: 'actionBarSkill',
    item: () => skill ? { skill, sourceBarIndex: barIndex, sourceSlotIndex: slotIndex } : null,
    canDrag: () => !state.isActionBarLocked && skill !== null,
    end: (item, monitor) => {
      // 如果拖拽结束时没有成功放置到任何drop target，则删除该技能
      if (!monitor.didDrop() && item) {
        const sourceActionBar = { ...state.actionBars[item.sourceBarIndex] };
        sourceActionBar.slots[item.sourceSlotIndex] = null;
        
        // 清除该槽位的键位绑定
        Object.keys(sourceActionBar.keyBindings).forEach(key => {
          if (sourceActionBar.keyBindings[key] === item.sourceSlotIndex) {
            delete sourceActionBar.keyBindings[key];
          }
        });
        
        updateActionBar(item.sourceBarIndex, sourceActionBar);
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });
  
  const [{ isOver }, drop] = useDrop({
    accept: ['skill', 'actionBarSkill'],
    drop: (item: { skill: Skill; sourceBarIndex?: number; sourceSlotIndex?: number }) => {
      const newActionBar = { ...state.actionBars[barIndex] };
      newActionBar.slots[slotIndex] = item.skill;
      updateActionBar(barIndex, newActionBar);
      
      // 如果是从操作栏拖拽的，清除原位置（只有在不是锁定状态或者是从技能面板拖拽时才处理）
      if (item.sourceBarIndex !== undefined && item.sourceSlotIndex !== undefined) {
        // 如果是操作栏间拖拽但操作栏已锁定，不清除原位置（相当于复制而不是移动）
        if (!state.isActionBarLocked) {
          const sourceActionBar = { ...state.actionBars[item.sourceBarIndex] };
          sourceActionBar.slots[item.sourceSlotIndex] = null;
          // 清除该槽位的键位绑定
          Object.keys(sourceActionBar.keyBindings).forEach(key => {
            if (sourceActionBar.keyBindings[key] === item.sourceSlotIndex) {
              delete sourceActionBar.keyBindings[key];
            }
          });
          updateActionBar(item.sourceBarIndex, sourceActionBar);
        }
      }
    },
    canDrop: () => true, // 始终允许拖入
    collect: (monitor) => ({
      isOver: monitor.isOver() && monitor.canDrop(),
    }),
  });

  const handleClick = () => {
    if (state.isKeybindMode) {
      const handleKeyPress = (event: KeyboardEvent) => {
        event.preventDefault();
        const key = event.key.toUpperCase();
        
        // 清除该槽位的现有绑定
        const currentActionBar = { ...state.actionBars[barIndex] };
        
        // 移除原有的键位绑定
        Object.keys(currentActionBar.keyBindings).forEach(existingKey => {
          if (currentActionBar.keyBindings[existingKey] === slotIndex) {
            delete currentActionBar.keyBindings[existingKey];
          }
        });
        
        // 如果按的是 Escape 或 Delete，只清除绑定不设置新的
        if (key === 'ESCAPE' || key === 'DELETE' || key === 'BACKSPACE') {
          updateActionBar(barIndex, currentActionBar);
        } else {
          // 移除其他槽位对这个按键的绑定
          Object.keys(currentActionBar.keyBindings).forEach(existingKey => {
            if (existingKey === key) {
              delete currentActionBar.keyBindings[existingKey];
            }
          });
          
          // 设置新的键位绑定
          currentActionBar.keyBindings[key] = slotIndex;
          updateActionBar(barIndex, currentActionBar);
        }
        
        document.removeEventListener('keydown', handleKeyPress);
      };
      
      const handleRightClick = (event: MouseEvent) => {
        event.preventDefault();
        // 右键点击清除绑定
        const currentActionBar = { ...state.actionBars[barIndex] };
        Object.keys(currentActionBar.keyBindings).forEach(existingKey => {
          if (currentActionBar.keyBindings[existingKey] === slotIndex) {
            delete currentActionBar.keyBindings[existingKey];
          }
        });
        updateActionBar(barIndex, currentActionBar);
        document.removeEventListener('keydown', handleKeyPress);
        document.removeEventListener('contextmenu', handleRightClick);
      };
      
      document.addEventListener('keydown', handleKeyPress);
      document.addEventListener('contextmenu', handleRightClick);
      
      // 5秒后自动取消监听
      setTimeout(() => {
        document.removeEventListener('keydown', handleKeyPress);
        document.removeEventListener('contextmenu', handleRightClick);
      }, 5000);
    } else if (skill) {
      onSkillUse(skill);
    }
  };

  // 合并拖拽 refs
  const dragDropRef = (node: HTMLElement | null) => {
    drop(node);
    drag(node);
  };

  return (
    <div
      ref={dragDropRef}
      style={{
        width: 64,
        height: 64,
        border: `2px solid ${
          isOver ? '#4A90E2' : 
          isDragging && state.isActionBarLocked ? '#FFA500' : // 锁定状态下拖拽显示橙色
          isDragging ? '#FFD700' : 
          state.isActionBarLocked && skill ? '#666' : // 锁定且有技能时显示灰色
          '#555'
        }`,
        borderRadius: '4px',
        backgroundColor: isOver ? '#4A90E2' : '#1a1a1a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        transition: 'all 0.2s ease',
        opacity: isDragging ? 0.5 : 1,
        cursor: skill && !state.isActionBarLocked ? 'move' : state.isKeybindMode ? 'pointer' : 'default',
        boxShadow: state.isActionBarLocked && skill ? 'inset 0 0 3px rgba(102,102,102,0.5)' : 'none' // 锁定时显示灰色内阴影
      }}
      onClick={handleClick}
    >
      {skill ? (
        <div style={{ pointerEvents: state.isActionBarLocked || state.isKeybindMode ? 'none' : 'auto' }}>
          <SkillIcon 
          skill={skill} 
          size={56} 
          keyBinding={keyBinding}
          isOnCooldown={isSkillOnCooldown(skill)}
          cooldownRemaining={getCooldownRemaining(skill)}
          onClick={handleClick}
          highlightType={getHighlightType ? getHighlightType(skill) : 'none'}
            highlightStyle={getHighlightType && getHighlightStyle ? getHighlightStyle(getHighlightType(skill)) : undefined}
            isDisabled={isSkillUsable ? !isSkillUsable(skill) : false}
            chargeInfo={getChargeInfo ? getChargeInfo(skill) : null}
          />
        </div>
      ) : (
        <div style={{ color: '#666', fontSize: '14px' }}>
          {slotIndex + 1}
        </div>
      )}
    </div>
  );
};

export const ActionBar: React.FC<ActionBarProps> = ({ 
  barIndex, 
  onSkillUse, 
  isSkillOnCooldown, 
  getCooldownRemaining,
  getHighlightType,
  getHighlightStyle,
  isSkillUsable,
  getChargeInfo
}) => {
  const { state } = useApp();
  const actionBar = state.actionBars[barIndex];

  const getKeyBinding = (slotIndex: number) => {
    for (const [key, slot] of Object.entries(actionBar.keyBindings)) {
      if (slot === slotIndex) {
        return key;
      }
    }
    return undefined;
  };

  return (
    <div style={{
      display: 'flex',
      gap: '6px',
      padding: '12px',
      backgroundColor: '#2a2a2a',
      borderRadius: '8px',
      border: '1px solid #444'
    }}>
      {actionBar.slots.map((skill, index) => (
        <ActionSlot
          key={index}
          skill={skill}
          slotIndex={index}
          barIndex={barIndex}
          keyBinding={getKeyBinding(index)}
          onSkillUse={onSkillUse}
          isSkillOnCooldown={isSkillOnCooldown}
          getCooldownRemaining={getCooldownRemaining}
          getHighlightType={getHighlightType}
          getHighlightStyle={getHighlightStyle}
          isSkillUsable={isSkillUsable}
          getChargeInfo={getChargeInfo}
        />
      ))}
    </div>
  );
};